import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  FileText, Clock, CheckCircle, XCircle, Phone, Mail, MapPin, Truck,
  Trash2, ArrowRight
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getStatusColor = (status) => {
  const colors = {
    pending: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
    contacted: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
    completed: 'text-green-400 bg-green-400/10 border-green-400/20',
    cancelled: 'text-red-400 bg-red-400/10 border-red-400/20',
  };
  return colors[status] || 'text-slate-400 bg-slate-400/10 border-slate-400/20';
};

const getServiceLabel = (service) => {
  const labels = { freight: 'Commercial Freight', moving: 'Residential Moving', delivery: 'On-Demand Delivery' };
  return labels[service] || service;
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const AdminQuotesTab = ({ quotes, stats, loading, onUpdateStatus, onDelete, onConvertToDelivery }) => {
  const [filter, setFilter] = useState('all');
  const filtered = filter === 'all' ? quotes : quotes.filter(q => q.status === filter);

  return (
    <>
      {/* Stats */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        {[
          { key: 'total', label: 'Total', icon: FileText, color: 'text-white' },
          { key: 'pending', label: 'Pending', icon: Clock, color: 'text-yellow-400' },
          { key: 'contacted', label: 'Contacted', icon: Phone, color: 'text-blue-400' },
          { key: 'completed', label: 'Completed', icon: CheckCircle, color: 'text-green-400' },
          { key: 'cancelled', label: 'Cancelled', icon: XCircle, color: 'text-red-400' },
        ].map(item => (
          <div key={item.key} className="bg-brand-surface rounded-xl border border-white/10 p-6" data-testid={`stat-${item.key}`}>
            <div className="flex items-center gap-3 mb-2">
              <item.icon className={`w-5 h-5 ${item.color}`} />
              <span className="text-slate-400 text-sm">{item.label}</span>
            </div>
            <p className={`text-3xl font-heading ${item.color}`}>{stats[item.key] || 0}</p>
          </div>
        ))}
      </motion.div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h2 className="font-heading text-2xl text-white">QUOTE <span className="text-brand-red">REQUESTS</span></h2>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white" data-testid="quote-filter-select">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent className="bg-brand-surface border-white/10">
            <SelectItem value="all" className="text-white hover:bg-white/10">All Quotes</SelectItem>
            <SelectItem value="pending" className="text-white hover:bg-white/10">Pending</SelectItem>
            <SelectItem value="contacted" className="text-white hover:bg-white/10">Contacted</SelectItem>
            <SelectItem value="completed" className="text-white hover:bg-white/10">Completed</SelectItem>
            <SelectItem value="cancelled" className="text-white hover:bg-white/10">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading quotes...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-brand-surface rounded-xl border border-white/10">
          <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">No quotes found</p>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
          {filtered.map((quote, index) => (
            <motion.div key={quote.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }} className="bg-brand-surface rounded-xl border border-white/10 p-6" data-testid={`quote-card-${quote.id}`}>
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                <div className="flex-grow">
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <h3 className="font-heading text-lg text-white">{quote.name}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(quote.status)}`}>{quote.status.toUpperCase()}</span>
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-brand-blue/10 text-brand-blue border border-brand-blue/20">{getServiceLabel(quote.service_type)}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-slate-400"><Phone className="w-4 h-4 text-brand-red" /><a href={`tel:${quote.phone}`} className="hover:text-white">{quote.phone}</a></div>
                    <div className="flex items-center gap-2 text-slate-400"><Mail className="w-4 h-4 text-brand-blue" /><a href={`mailto:${quote.email}`} className="hover:text-white">{quote.email}</a></div>
                    <div className="flex items-center gap-2 text-slate-400"><Clock className="w-4 h-4 text-slate-500" /><span>{formatDate(quote.created_at)}</span></div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" /><div><span className="text-xs text-slate-500">Pickup</span><p className="text-slate-300">{quote.pickup_location}</p></div></div>
                    <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-brand-red mt-1 flex-shrink-0" /><div><span className="text-xs text-slate-500">Delivery</span><p className="text-slate-300">{quote.delivery_location}</p></div></div>
                  </div>
                  {quote.load_details && (
                    <div className="mt-4 flex items-start gap-2"><Truck className="w-4 h-4 text-slate-500 mt-1 flex-shrink-0" /><div><span className="text-xs text-slate-500">Load Details</span><p className="text-slate-400">{quote.load_details}</p></div></div>
                  )}
                  {quote.preferred_date && <div className="mt-2 text-sm text-slate-500">Preferred date: {quote.preferred_date}</div>}
                </div>
                <div className="flex flex-row lg:flex-col gap-2 flex-shrink-0">
                  <Select value={quote.status} onValueChange={(value) => onUpdateStatus(quote.id, value)}>
                    <SelectTrigger className="w-36 bg-white/5 border-white/10 text-white" data-testid={`status-select-${quote.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-brand-surface border-white/10">
                      <SelectItem value="pending" className="text-yellow-400 hover:bg-white/10">Pending</SelectItem>
                      <SelectItem value="contacted" className="text-blue-400 hover:bg-white/10">Contacted</SelectItem>
                      <SelectItem value="completed" className="text-green-400 hover:bg-white/10">Completed</SelectItem>
                      <SelectItem value="cancelled" className="text-red-400 hover:bg-white/10">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                  <button onClick={() => onConvertToDelivery(quote)}
                    className="p-2 rounded-lg bg-brand-blue/10 border border-brand-blue/20 text-brand-blue hover:bg-brand-blue/20 transition-colors flex items-center gap-1.5 text-xs font-medium"
                    data-testid={`convert-quote-${quote.id}`} title="Convert to Delivery Job">
                    <ArrowRight className="w-4 h-4" /><span className="hidden lg:inline">Delivery</span>
                  </button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-colors" data-testid={`delete-quote-${quote.id}`}><Trash2 className="w-5 h-5" /></button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-brand-surface border-white/10">
                      <AlertDialogHeader><AlertDialogTitle className="text-white">Delete Quote?</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">This will permanently delete the quote from {quote.name}.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => onDelete(quote.id)} className="bg-red-600 text-white hover:bg-red-700" data-testid={`confirm-delete-quote-${quote.id}`}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}
    </>
  );
};

export default AdminQuotesTab;
